/**
 * Спецификация отрисовки карточки: тип графика, режим значения, сортировка, размер, секция.
 *
 * Сейчас сервер отдаёт только `chartType` из `NodeChartTypeResolver`, и этих семи строк
 * не хватает: пять из них означают «нарисуй столбцы», а нужное решение (кольцо или
 * столбцы, один цвет или палитра, стат-плитка вместо графика с одной категорией) зависит
 * от кардинальности выхода, которая в каталоге уже есть — в `options`/`items`.
 *
 * Поэтому спека выводится здесь, на клиенте. Когда сервер научится присылать `chart.spec`
 * (объявленную автором пакета), эта функция останется фолбэком: `chart.spec ?? deriveChartSpec(chart)`.
 */

export const CHART_TYPES = {
  Bar: 'bar',
  Donut: 'donut',
  BooleanGauge: 'boolean_gauge',
  Funnel: 'funnel',
  ChecklistTable: 'checklist_table',
  Stat: 'stat',
  QuoteList: 'quote_list'
};

export const SECTIONS = {
  Distribution: 'distribution',
  Quality: 'quality',
  Texts: 'texts'
};

/**
 * Кольцо читается, пока сектора различимы и легенда влезает в две строки. На демо-стенде
 * «Сценарии звонков» — восемь секторов, это и есть практический потолок.
 */
const DONUT_MAX_OPTIONS = 8;

const DEFAULT_SPEC = {
  type: CHART_TYPES.Bar,
  value: 'calls',
  sort: 'value_desc',
  top: null,
  otherBucket: false,
  fillDeclared: false,
  tone: 'categorical',
  size: 'md',
  section: SECTIONS.Distribution,
  hidden: false
};

const countOptions = (chart) => chart.options?.length ?? 0;

/**
 * Серверные типы графиков (`NodeChartTypes`) — вход, а не результат: они говорят про вид
 * данных, а не про то, чем их рисовать.
 */
const deriveByServerType = (chart) => {
  switch (chart.chartType) {
    case 'checklist_heatmap':
      return {
        type: CHART_TYPES.ChecklistTable,
        section: SECTIONS.Quality,
        size: 'full',
        sort: 'declared'
      };

    case 'funnel':
      // Этапы идут в объявленном порядке: сортировка по убыванию превратила бы воронку
      // в обычный рейтинг и убила бы смысл пошаговой конверсии. Пропущенный за период
      // этап добираем нулём, иначе цепочка конверсии считается между не соседними этапами.
      return {
        type: CHART_TYPES.Funnel,
        sort: 'declared',
        fillDeclared: true,
        tone: 'progress',
        size: 'md'
      };

    case 'boolean_share':
      return { type: CHART_TYPES.BooleanGauge, value: 'share', tone: 'semantic', size: 'sm' };

    case 'multi_label_bar':
    case 'object_list_bar':
      // Один показатель по разным категориям: раскрашивать столбцы в разные цвета —
      // значит намекать на несуществующую разницу между ними.
      return {
        type: CHART_TYPES.Bar,
        value: 'calls',
        sort: 'value_desc',
        top: 12,
        otherBucket: true,
        tone: 'sequential'
      };

    case 'categorical_bar': {
      const options = countOptions(chart);

      if (options <= 1) {
        return { type: CHART_TYPES.Stat, value: 'share', size: 'sm' };
      }

      if (options <= DONUT_MAX_OPTIONS) {
        // В кольце цвет — единственный идентификатор сектора, поэтому палитра.
        return { type: CHART_TYPES.Donut, value: 'share', sort: 'value_desc', tone: 'categorical' };
      }

      // В столбцах категорию называет подпись оси, и цвет не несёт информации:
      // радуга из семи цветов только добавляла шум и намекала на разницу между
      // категориями, которой нет.
      return {
        type: CHART_TYPES.Bar,
        sort: 'value_desc',
        top: 10,
        otherBucket: true,
        tone: 'sequential'
      };
    }

    case 'text_samples':
      // Пока у цитат нет своей карточки — выход не рисуем, как и раньше.
      return { type: CHART_TYPES.QuoteList, section: SECTIONS.Texts, size: 'full', hidden: true };

    default:
      return {};
  }
};

export const deriveChartSpec = (chart) => ({
  ...DEFAULT_SPEC,
  ...deriveByServerType(chart)
});

/** Спека карточки: объявленная сервером, иначе выведенная из вида выхода. */
export const resolveChartSpec = (chart) => (chart?.spec ? { ...DEFAULT_SPEC, ...chart.spec } : deriveChartSpec(chart));

/**
 * Виды карточки: помимо основного графика почти у каждого выхода осмысленна динамика.
 *
 * Эндпоинт `/analytics/nodes/timeseries` принимает те же параметры, что и агрегат,
 * поэтому переключатель ставится в оболочку карточки и достаётся сразу всем
 * распределениям, а не только специально объявленным «графикам динамики».
 */
export const VIEWS = {
  Distribution: 'distribution',
  Dynamics: 'dynamics',
  Heatmap: 'heatmap',
  Table: 'table'
};

const DISTRIBUTION_TYPES = new Set([
  CHART_TYPES.Bar,
  CHART_TYPES.Donut,
  CHART_TYPES.BooleanGauge,
  CHART_TYPES.Funnel
]);

export const resolveViews = (spec) => {
  if (spec.type === CHART_TYPES.ChecklistTable) {
    return [
      { code: VIEWS.Heatmap, label: 'Тепловая карта' },
      { code: VIEWS.Table, label: 'По пунктам' }
    ];
  }

  if (DISTRIBUTION_TYPES.has(spec.type)) {
    return [
      { code: VIEWS.Distribution, label: 'Распределение' },
      { code: VIEWS.Dynamics, label: 'Динамика' }
    ];
  }

  return [];
};

const DEGENERATE_CODES = new Set(['—', 'unknown', '__other']);

/**
 * Вырожденное распределение — это не график, а одно число.
 *
 * «Причина несодержательного звонка» объявляет четыре класса, но за период встречается
 * только «Не задано»: столбчатая карточка с одним столбцом занимает половину ряда и
 * не сообщает ничего. Решение принимается по данным, а не по каталогу, поэтому живёт
 * здесь, а не в `deriveChartSpec`.
 */
export const isDegenerateDistribution = (rows = []) => {
  if (rows.length === 0) {
    return true;
  }

  return rows.length === 1 && DEGENERATE_CODES.has(String(rows[0].code));
};
